"use client"

import {useUiControlLabels} from "@/components/i18n/ui-control-labels-context"
import { Fragment, useId, type ComponentProps, type ReactNode } from "react"
import Link from "next/link"
import { useField, useFormikContext } from "formik"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"

type FormSelectOption<TValue extends string> = {
  value: TValue
  label: string
  icon?: ReactNode
  disabled?: boolean
  description?: string
  action?: { label: string; href: string }
}

type FormSelectProps<TValue extends string> = {
  name: string
  label: string
  options: readonly FormSelectOption<TValue>[]
  description?: string
  placeholder?: string
  emptyMessage?: string
  disabled?: boolean
  onValueChange?: (value: TValue) => void
  separateOptions?: boolean
  contentClassName?: string
  contentProps?: Pick<ComponentProps<typeof SelectContent>, "side" | "avoidCollisions">
  resetOnChange?: readonly string[]
}

export function FormSelect<TValue extends string>({
  name,
  label,
  options,
  description,
  placeholder,
  emptyMessage,
  disabled = false,
  onValueChange,
  resetOnChange,
  separateOptions = false,
  contentClassName,
  contentProps,
}: FormSelectProps<TValue>) {
  const inputId = useId()
  const {fieldError} = useUiControlLabels()
  const [field, meta] = useField<TValue>(name)
  const { setFieldTouched, setFieldValue, setValues } = useFormikContext<Record<string, unknown>>()
  const error = meta.touched && meta.error ? meta.error : null

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label htmlFor={inputId}>{label}</Label>
      </div>
      <Select
        disabled={disabled}
        value={field.value}
        onValueChange={(value) => {
          if (resetOnChange?.length) {
            void setValues(current => {
              const next = { ...current, [name]: value }
              for (const fieldName of resetOnChange) next[fieldName] = ""
              return next
            })
          } else {
            void setFieldValue(name, value)
          }
          void setFieldTouched(name, true, false)
          onValueChange?.(value as TValue)
        }}
      >
        <SelectTrigger id={inputId} aria-invalid={Boolean(error)}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent {...contentProps} emptyMessage={emptyMessage} className={cn("max-w-[calc(100vw-2rem)]", contentClassName)}
          onKeyDown={event => {
            // Radix suppresses Tab in a listbox; make the setup links reachable.
            if (event.key !== "Tab") return
            const links = Array.from(event.currentTarget.querySelectorAll<HTMLAnchorElement>("a[href]"))
            if (!links.length) return
            event.preventDefault()
            const currentIndex = links.findIndex(link => link === document.activeElement)
            const step = event.shiftKey ? -1 : 1
            let nextIndex = (currentIndex + step + links.length) % links.length
            if (currentIndex < 0) nextIndex = event.shiftKey ? links.length - 1 : 0
            links[nextIndex].focus()
          }}>
          {options.map((option, index) => (
            <Fragment key={option.value}>
            {separateOptions && index > 0 ? <SelectSeparator className="mx-3 my-1 bg-border" /> : null}
            <div>
            <SelectItem value={option.value} disabled={option.disabled} description={option.description ? <span className="block max-w-lg whitespace-normal break-words">{option.description}</span> : undefined}
              aria-description={option.description}
              className={option.disabled && option.description ? "data-[disabled]:opacity-100 data-[disabled]:text-muted-foreground" : undefined}>
              {option.icon ? <span className="flex min-w-0 items-center gap-2">{option.icon}<span>{option.label}</span></span> : option.label}
            </SelectItem>
            {option.disabled && option.action ? (
              <Link href={option.action.href} className="mb-1.5 ml-8 inline-block text-xs text-blue-600 underline underline-offset-2 focus-visible:outline focus-visible:outline-2"
                onPointerDown={event => event.stopPropagation()}
                onClick={event => event.stopPropagation()}>
                <span>{option.action.label}</span>
              </Link>
            ) : null}
            </div>
            </Fragment>
          ))}
        </SelectContent>
      </Select>
      {description ? <p className="text-xs text-muted-foreground">{description}</p> : null}
      {error ? <p key={fieldError(error)} className="text-xs font-medium text-destructive">{fieldError(error)}</p> : null}
    </div>
  )
}
